import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmationModal = ({
  title,
  message,
  confirmText = 'Confirm',
  confirmStyle = 'danger', // 'danger' or 'primary'
  onConfirm,
  onCancel,
  record = null,
  schema = null
}) => {
  const confirmButtonClasses =
    confirmStyle === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-blue-600 hover:bg-blue-700 text-white';

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <div className={`mr-3 ${confirmStyle === 'danger' ? 'text-red-600' : 'text-blue-600'}`}>
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-700 mb-4">{message}</p>

          {/* Record Preview */}
          {record && schema && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">
                Record Preview
              </h4>
              <div className="space-y-1">
                {schema.columns.slice(0, 5).map((column) => {
                  const value = record[column.name];
                  let displayValue = value;

                  if (value === null || value === undefined) {
                    displayValue = <span className="text-gray-400 italic">null</span>;
                  } else if (typeof value === 'boolean') {
                    displayValue = value ? 'true' : 'false';
                  } else if (typeof value === 'object') {
                    displayValue = JSON.stringify(value);
                  } else if (typeof value === 'string' && value.length > 50) {
                    displayValue = value.substring(0, 50) + '...';
                  }

                  return (
                    <div key={column.name} className="flex items-start text-xs">
                      <span className="font-medium text-gray-700 w-1/3">
                        {column.name}:
                      </span>
                      <span className="text-gray-900 w-2/3 break-words">{displayValue}</span>
                    </div>
                  );
                })}
                {schema.columns.length > 5 && (
                  <p className="text-xs text-gray-500 italic">
                    ... and {schema.columns.length - 5} more fields
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              ⚠️ This action will be logged in the audit trail and cannot be undone.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${confirmButtonClasses}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
